<template>
  <div class="card shadow-sm table-card mb-4">
    <div class="form-card-header flex-wrap">
      <div class="section-number"><i class="fe fe-map" style="font-size: 12px;"></i></div>
      <div class="flex-grow-1">
        <h6 class="mb-0 font-weight-bold text-white">{{ title }}{{ stateName ? ` · ${stateName}` : "" }}</h6>
        <small style="color: rgba(255,255,255,.75);">
          {{ level === "ward" ? `Wards of ${lgaName || "the state"}` : "By local government area" }} · {{ subtitle }}
        </small>
      </div>
    </div>
    <div class="card-body">
      <div class="d-flex flex-wrap align-items-center mb-3" style="gap: 8px;">
        <button v-if="level === 'ward' && lgaId" class="btn btn-sm btn-outline-secondary" @click="showLgas"><i class="fe fe-arrow-left mr-1"></i> All LGAs</button>
        <select class="form-control form-control-sm" style="width: auto;" v-model="lgaPick" @change="pickLga">
          <option :value="null">LGA view</option>
          <option v-for="r in lgaRows" :key="r.id" :value="r.id">Wards of {{ r.name }}</option>
          <option value="all-wards">All wards in the state</option>
        </select>
        <v-select v-if="showSectors" class="sector-select" multiple v-model="sectors" :options="sectorOptions" placeholder="All programmes" @input="load" />
        <div v-if="hasPopulation" class="btn-group btn-group-sm" role="group" aria-label="Map shows">
          <button class="btn" :class="metric === 'count' ? 'btn-primary' : 'btn-outline-primary'" @click="setMetric('count')">{{ unit.charAt(0).toUpperCase() + unit.slice(1) }}</button>
          <button class="btn" :class="metric === 'coverage' ? 'btn-primary' : 'btn-outline-primary'" @click="setMetric('coverage')">% of population covered</button>
        </div>
        <small class="text-muted ml-auto">Click an LGA to see its wards.</small>
      </div>

      <div v-if="population && population.total" class="coverage-strip mb-3">
        <div><span class="stat-label">Population{{ population.year ? ` (${population.year})` : "" }}</span><div class="stat-value">{{ population.total.toLocaleString() }}</div></div>
        <div><span class="stat-label">Covered</span><div class="stat-value">{{ population.enrollees.toLocaleString() }}</div></div>
        <div><span class="stat-label">Coverage</span><div class="stat-value" :class="coverageClass(population.coverage_pct)">{{ population.coverage_pct }}%</div></div>
        <div><span class="stat-label">Not yet covered</span><div class="stat-value">{{ Math.max(0, population.total - population.enrollees).toLocaleString() }}</div></div>
        <div class="flex-grow-1" style="min-width: 180px;">
          <span class="stat-label">Towards universal coverage</span>
          <div class="progress mt-1" style="height: 10px;"><div class="progress-bar bg-success" :style="{ width: Math.min(100, population.coverage_pct) + '%' }"></div></div>
          <small class="text-muted">
            <span v-if="population.wards_with_population < population.wards">Population known for {{ population.wards_with_population }} of {{ population.wards }} wards. </span>
            <span v-if="population.filtered">Filtered by programme/period. </span>
            <router-link v-if="showSectors" to="/population">Population figures</router-link>
          </small>
        </div>
      </div>
      <div v-else-if="population && showSectors" class="small text-muted mb-2">
        <i class="fe fe-info mr-1"></i>No population figures yet, so coverage can't be shown. <router-link to="/population">Enter ward populations</router-link>.
      </div>

      <div v-if="loading && !rows.length" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div>
      <div v-else-if="error" class="empty-state"><i class="fe fe-map"></i>{{ error }}</div>
      <div v-else class="row">
        <div class="col-lg-8">
          <highcharts :constructor-type="'mapChart'" :options="mapOptions" :key="mapKey" style="height: 520px;" />
          <p class="small text-muted mb-0">{{ credit }}</p>
        </div>
        <div class="col-lg-4">
          <div class="d-flex justify-content-between small text-muted mb-1">
            <span>{{ rows.filter((r) => r.total > 0).length }} {{ level === "ward" ? "wards" : "LGAs" }} with {{ unit }}</span>
            <span v-if="notLocated">{{ notLocated.toLocaleString() }} without a valid {{ level === "ward" ? "ward" : "LGA" }}</span>
          </div>
          <div class="table-responsive area-table">
            <table class="table table-sm mb-0">
              <thead class="table-head"><tr><th class="table-th">{{ level === "ward" ? "Ward" : "LGA" }}</th><th class="table-th text-right">{{ unit.charAt(0).toUpperCase() + unit.slice(1) }}</th>
                <th class="table-th text-right" v-if="!hasPopulation">Share</th>
                <template v-else><th class="table-th text-right">Population</th><th class="table-th text-right">Coverage</th></template></tr></thead>
              <tbody>
                <tr class="table-row" v-for="r in sortedRows" :key="r.id" :class="{ 'text-muted': !r.matched }" style="cursor: pointer;" @click="level === 'lga' && drill(r)">
                  <td>{{ r.name }}<span v-if="level === 'ward' && !lgaId && r.lga" class="small text-muted"> · {{ r.lga }}</span>
                    <i v-if="!r.matched" class="fe fe-map-pin ml-1" title="Not found on the map"></i></td>
                  <td class="text-right">{{ r.total.toLocaleString() }}</td>
                  <td class="text-right" v-if="!hasPopulation">{{ total ? ((100 * r.total) / total).toFixed(1) : 0 }}%</td>
                  <template v-else>
                    <td class="text-right">{{ r.population ? r.population.toLocaleString() : "—" }}</td>
                    <td class="text-right" :class="coverageClass(r.coverage_pct)">{{ r.coverage_pct === null || r.coverage_pct === undefined ? "—" : r.coverage_pct + "%" }}</td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { stateSlug, loadBoundaries, matchFeature, attribution } from "./geo";

/** Choropleth of enrollees per LGA / ward (GET analytics/enrollment/geography + public/geo boundaries). */
export default {
  name: "EnrolleeMap",
  props: {
    // Defaults: the enrolment map. Disease tracking passes its own endpoint (same response shape).
    endpoint: { type: String, default: "/api/v1/auth/analytics/enrollment/geography" },
    extraParams: { type: Object, default: () => ({}) },
    title: { type: String, default: "Where enrollees live" },
    subtitle: { type: String, default: "principals and dependants (by their principal's address)" },
    unit: { type: String, default: "enrollees" },
    showSectors: { type: Boolean, default: true },
    initialMetric: { type: String, default: "count" }, // count | coverage (needs ward populations)
  },
  data() {
    return {
      level: "lga",
      lgaId: null,
      lgaName: "",
      lgaPick: null,
      lgaRows: [],
      sectors: [],
      sectorOptions: [],
      rows: [],
      features: [],
      notLocated: 0,
      stateName: "",
      slug: null,
      credit: "",
      loading: false,
      error: "",
      mapKey: 0,
      metric: this.initialMetric,
      population: null,
    };
  },
  computed: {
    hasPopulation() {
      return !!(this.population && this.population.total);
    },
    sortedRows() {
      if (this.metric !== "coverage") return this.rows;
      return this.rows.slice().sort((a, b) => (a.coverage_pct === null || a.coverage_pct === undefined ? 1e9 : a.coverage_pct) - (b.coverage_pct === null || b.coverage_pct === undefined ? 1e9 : b.coverage_pct));
    },
    total() {
      return this.rows.reduce((n, r) => n + r.total, 0);
    },
    mapOptions() {
      const vm = this;
      const cov = this.metric === "coverage" && this.hasPopulation;
      const data = this.features.map((f, i) => ({
        k: i, name: f.name, rowId: f.rowId, count: f.value === undefined ? null : f.value, population: f.population || null,
        value: cov ? (f.population ? Math.round((10000 * (f.value || 0)) / f.population) / 100 : null) : f.value === undefined ? null : f.value,
      }));
      return {
        chart: { map: null, backgroundColor: "transparent" },
        title: { text: null },
        credits: { enabled: false },
        mapNavigation: { enabled: true, buttonOptions: { verticalAlign: "bottom" } },
        colorAxis: cov
          ? { min: 0, stops: [[0, "#fee2e2"], [0.4, "#fde68a"], [1, "#15803d"]], labels: { format: "{value}%" } }
          : { min: 0, minColor: "#e0f2fe", maxColor: "#1e3a8a", type: "linear" },
        legend: { layout: "horizontal", align: "center", verticalAlign: "bottom" },
        tooltip: {
          formatter() {
            const p = this.point;
            const count = p.count === null || p.count === undefined ? "None" : p.count.toLocaleString() + " " + vm.unit;
            if (!vm.hasPopulation) return `<b>${p.name}</b><br/>${count}`;
            return `<b>${p.name}</b><br/>${count}<br/>Population: ${p.population ? p.population.toLocaleString() : "not recorded"}`
              + (p.population ? `<br/>Coverage: <b>${(Math.round((10000 * (p.count || 0)) / p.population) / 100)}%</b>` : "");
          },
        },
        series: [{
          type: "map",
          name: this.unit,
          mapData: this.features.map((f, i) => ({ k: i, name: f.name, path: f.path })),
          data,
          joinBy: ["k", "k"],
          nullColor: "#f1f5f9",
          borderColor: "#ffffff",
          borderWidth: 0.6,
          states: { hover: { color: "#f59e0b" } },
          cursor: this.level === "lga" ? "pointer" : "default",
          point: { events: { click() { if (vm.level === "lga" && this.rowId) vm.drill(vm.rows.find((r) => r.id === this.rowId)); } } },
        }],
      };
    },
  },
  watch: {
    extraParams: { deep: true, handler() { this.load(); } },
  },
  async mounted() {
    if (this.showSectors) this.$batchGet("/api/v1/auth/analytics/enrollment/sectors").then((res) => {
      this.sectorOptions = (res.data.data.rows || []).map((r) => r.sector);
    }).catch(() => {});
    attribution().then((a) => { this.credit = a; }).catch(() => {});
    this.load();
  },
  methods: {
    setMetric(m) {
      this.metric = m;
      this.mapKey++;
    },
    coverageClass(pct) {
      if (pct === null || pct === undefined) return "";
      return pct >= 50 ? "text-success" : pct >= 20 ? "text-warning" : "text-danger";
    },
    showLgas() {
      this.level = "lga";
      this.lgaId = null;
      this.lgaName = "";
      this.lgaPick = null;
      this.load();
    },
    pickLga() {
      if (this.lgaPick === null) return this.showLgas();
      if (this.lgaPick === "all-wards") {
        this.level = "ward";
        this.lgaId = null;
        this.lgaName = "";
        return this.load();
      }
      this.drill(this.lgaRows.find((r) => r.id === this.lgaPick));
    },
    drill(row) {
      if (!row) return;
      this.level = "ward";
      this.lgaId = row.id;
      this.lgaName = row.name;
      this.lgaPick = row.id;
      this.load();
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const res = await this.$batchGet(this.endpoint, {
          params: { ...this.extraParams, level: this.level, lga_id: this.lgaId || undefined, sectors: this.showSectors ? this.sectors : undefined },
        });
        const d = res.data.data;
        this.notLocated = d.not_located;
        this.population = d.population || null;
        if (!this.hasPopulation) this.metric = "count";
        if (d.state && !this.slug) {
          this.stateName = d.state.name;
          this.slug = await stateSlug(d.state.name);
        }
        if (!this.slug) throw new Error("No map is available for this state yet.");
        let features = await loadBoundaries(this.level, this.slug);
        if (this.level === "ward" && this.lgaName) {
          const lgaFeature = matchFeature((await loadBoundaries("lga", this.slug)).map((f) => ({ name: f.name })), this.lgaName);
          const target = lgaFeature ? lgaFeature.name : this.lgaName;
          const inLga = features.filter((f) => matchFeature([{ name: f.lga }], target));
          if (inLga.length) features = inLga;
        }
        features = features.map((f) => ({ ...f, value: undefined, rowId: null }));
        const rows = d.rows.map((r) => {
          const f = matchFeature(features, r.name, this.level === "ward" ? r.lga || this.lgaName : null);
          if (f) {
            f.value = (f.value || 0) + r.total;
            if (r.population) f.population = (f.population || 0) + r.population;
            f.rowId = r.id;
          }
          return { ...r, matched: !!f };
        });
        this.features = features;
        this.rows = rows;
        if (this.level === "lga") this.lgaRows = rows.filter((r) => r.total > 0 || r.population).slice().sort((a, b) => a.name.localeCompare(b.name));
        this.mapKey++;
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message || "Could not load the map.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
@import "../../reports/reports.css";
.sector-select { min-width: 220px; max-width: 420px; font-size: 0.85rem; }
.area-table { max-height: 520px; overflow-y: auto; }
.coverage-strip { display: flex; flex-wrap: wrap; gap: 18px; align-items: flex-start; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; }
</style>
